import { FunctionComponent, useContext, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import * as vp from 'vopay';
import AgreePADContext from './AgreePADContext';
import styles from './AgreePADDialogue.module.scss';
import { AgreePADDialogueHandler, DialogueHandler } from './types';

const { Modal, Button } = vp;

type AgreePADDialogueProps = {
  handleDialogue?: DialogueHandler<AgreePADDialogueHandler>;
  // deprecating 'handleDialogue' for the existing payment method flow
  isOpen?: boolean;
  onClose?: (val: boolean) => void;
  onNext?: () => void;
};

type FC = FunctionComponent<AgreePADDialogueProps>;

const AgreePADDialogue: FC = ({ handleDialogue, isOpen, onClose, onNext }: AgreePADDialogueProps) => {
  const { showAgreePADSuccessAlert } = useContext(AgreePADContext);
  const reportTemplateRef = useRef(null);
  const { t, i18n } = useTranslation('translations', { keyPrefix: 'PreAuthorizedDebit' });

  return useMemo(
    () => (
      <div>
        {/* Agree PAD Agreement Error */}
        <Modal isVisible={!!(showAgreePADSuccessAlert || isOpen)} enableBgClick={false}>
          <div className={styles.TermsModal}>
            <h2 className={styles.TermsModalHeader}>{t('titleCreditCard')}</h2>
            <div className={styles.TermsConatinerWrapper}>
              <div className={styles.TermsConatiner}>
                {i18n.language === 'en' && (
                  <div ref={reportTemplateRef}>
                    By submitting this transaction or saving your credit card, you authorize Payee, and, if applicable,
                    your Payee’s affiliated entities to debit the car specified for any amount owed for charges arising
                    from your use of the Payee’s services and/or purchase of products from the Payee, pursuant to the
                    Payee’s website and terms, until this authorization is revoked.
                    <br />
                    <br />
                    If you use the Payee’s services or purchase more than one of the Payee’s products periodically
                    pursuant to Payee’s terms, you authorize this Payee to debit your card periodically. Payments that
                    fall outside of the periodic debits authorized above will only be debited after your authorization
                    is obtained.
                    <br />
                    <br />
                    You may amend or cancel this authorization at any time by providing written notice to the Payee with
                    30 (thirty) days notice prior to the scheduled debit.
                  </div>
                )}
                {i18n.language === 'fr' && (
                  <div ref={reportTemplateRef}>
                    En soumettant cette transaction ou en enregistrant votre carte de crédit, vous autorisez le
                    Bénéficiaire et, le cas échéant, les entités affiliées au Bénéficiaire à débiter la carte spécifiée
                    de tout montant dû pour des frais découlant de votre utilisation des services du Bénéficiaire et/ou
                    de l&apos;achat de produits auprès du Bénéficiaire, conformément au site web et aux modalités et
                    conditions du Bénéficiaire, jusqu&apos;à ce que cette autorisation soit révoquée.
                    <br />
                    <br />
                    Si vous utilisez les services du Bénéficiaire ou achetez périodiquement plus d&apos;un produit du
                    Bénéficiaire conformément aux modalités et conditions du Bénéficiaire, vous autorisez ce dernier à
                    débiter périodiquement votre carte. Les paiements qui n&apos;entrent pas dans le cadre des débits
                    périodiques autorisés ci-dessus ne seront débités qu&apos;après obtention de votre autorisation.
                    <br />
                    <br />
                    Vous pouvez modifier ou annuler cette autorisation à tout moment en adressant une notification
                    écrite au bénéficiaire avec un préavis de 30 (trente) jours avant le débit prévu.
                  </div>
                )}
              </div>
            </div>

            {/* //Hiding temporarily as it doesnt account for custom PAD's
            <Box pt="sm">
              <Term
                term={
                  <>
                    <a href="https://vopay.com/ca/legal/credit-card-pad/" target="_blank" rel="noreferrer">
                      {t('termsNewWindowLink')}
                    </a>
                  </>
                }
              />
            </Box> */}

            <div className={styles.ButtonBar}>
              <Button
                id="pre-authorized-debit-accept"
                color="dark"
                round
                bold
                noShadow
                size="xlg"
                label={`${t('continueButton')}`}
                width="100%"
                onClick={() => {
                  handleDialogue && handleDialogue({ type: 'agree', state: 'success' });
                  onNext && onNext();
                }}
              />
            </div>

            <div className={styles.CrossContainer}>
              <Button
                id='pre-authorized-debit-close"'
                color="secondary"
                label=""
                iconLocation="center"
                icon="cross"
                iconSize="md"
                iconWidth="17px"
                background="none"
                noShadow
                onClick={() => {
                  handleDialogue && handleDialogue({ type: 'agree', state: 'fail' });
                  onClose && onClose(false);
                }}
              />
            </div>
          </div>
        </Modal>
      </div>
    ),
    [showAgreePADSuccessAlert, isOpen],
  );
};

export default AgreePADDialogue;
